import useWindowSize from "@shared/hooks/useWindowSize";
import breakpoints from "@shared/theme/props/breakpoints";
import Drawer from "../Drawer/Drawer.web";
import Modal from "../Modal/Modal.web";
import type { ResponsiveDialogSharedProps } from "./ResponsiveDialog.props";

function getIsMobile(
  forceDisplayMode: "drawer" | "modal" | undefined,
  autoDetectedMobile: boolean | undefined
): boolean | undefined {
  if (forceDisplayMode === "drawer") return true;
  if (forceDisplayMode === "modal") return false;
  return autoDetectedMobile;
}

/**
 * A responsive dialog component that renders a modal on desktop and a drawer on mobile.
 *
 * Features:
 * - Automatically switches between Modal (desktop) and Drawer (mobile) based on viewport width
 * - Supports custom z-index for nested modal contexts
 * - Consistent API for header, body, and footer across both modes
 *
 * @example
 * ```tsx
 * <ResponsiveDialog
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   headerComponent={<Text>Title</Text>}
 *   bodyComponent={<Content />}
 *   footerComponent={<Button onClick={onClose}>Close</Button>}
 *   // For nested modals, set a higher z-index
 *   zIndex={1500}
 * />
 * ```
 */
export default function ResponsiveDialog({
  headerComponent,
  headerContainerProps,
  bodyComponent,
  bodyContainerProps,
  footerComponent,
  footerContainerProps,
  overlayComponent,
  isOpen,
  onClose,
  drawerContentProps,
  drawerOverlayProps,
  modalContentProps,
  zIndex,
  forceDisplayMode,
  ...props
}: ResponsiveDialogProps): JSX.Element {
  const { width } = useWindowSize();
  const autoDetectedMobile = width != null ? width < breakpoints.sm : undefined;
  const isMobile = getIsMobile(forceDisplayMode, autoDetectedMobile);

  // Calculate z-indexes for overlay and content
  const overlayZIndex = zIndex ? zIndex - 1 : undefined;
  const contentZIndex = zIndex;

  if (isMobile) {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        drawerContentProps={{
          ...drawerContentProps,
          ...(contentZIndex && { zIndex: contentZIndex }),
        }}
        {...props}
      >
        {overlayComponent ?? (
          <Drawer.Overlay
            {...drawerOverlayProps}
            {...(overlayZIndex && { zIndex: overlayZIndex })}
          />
        )}
        <Drawer.Header {...headerContainerProps}>
          {headerComponent}
        </Drawer.Header>
        <Drawer.Body {...bodyContainerProps}>{bodyComponent}</Drawer.Body>
        {footerComponent && (
          <Drawer.Footer {...footerContainerProps}>
            {footerComponent}
          </Drawer.Footer>
        )}
      </Drawer>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalContentProps={{
        ...modalContentProps,
        ...(contentZIndex && { zIndex: contentZIndex }),
      }}
      {...props}
    >
      {overlayComponent ?? (
        <Modal.Overlay
          {...(overlayZIndex != null && {
            zIndex: overlayZIndex as string | number,
          })}
        />
      )}
      <Modal.Header {...headerContainerProps}>{headerComponent}</Modal.Header>
      <Modal.Body {...bodyContainerProps}>{bodyComponent}</Modal.Body>
      {footerComponent && (
        <Modal.Footer {...footerContainerProps}>{footerComponent}</Modal.Footer>
      )}
    </Modal>
  );
}

export type ResponsiveDialogProps = ResponsiveDialogSharedProps & {
  // Add any web specific props here.
};
